import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, setSessionEndedHandler } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import type { AuthUser } from "@/types";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissionKeys: string[]) => boolean;
  hasAllPermissions: (permissionKeys: string[]) => boolean;
  getScope: (resource: string) => string;
  login: (emailOrUsername: string, password: string, rememberMe?: boolean) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const extractUser = (res: any): AuthUser | null => {
    if (!res) return null;
    const candidate = res.data?.user || res.data || res.user || res;
    if (
      candidate &&
      (candidate.id ||
        candidate.Id ||
        candidate.user_id ||
        candidate.email ||
        candidate.Email ||
        candidate.username ||
        candidate.UserName)
    ) {
      const roleCode = String(
        candidate.user_role_code ||
        candidate.role ||
        candidate.user_role?.code ||
        candidate.UserRole?.RoleCode ||
        ""
      ).toUpperCase();

      return {
        Id: candidate.id || candidate.Id || candidate.user_id,
        FirstName: candidate.firstName || candidate.first_name || candidate.FirstName || "Admin",
        LastName: candidate.lastName || candidate.last_name || candidate.LastName || "",
        Email: candidate.email || candidate.Email || "",
        UserName: candidate.username || candidate.UserName || "",
        UserRoleId: candidate.user_role_id || candidate.UserRoleId || 0,
        UserRole: candidate.user_role ||
          candidate.UserRole || {
            Id: candidate.user_role_id || 0,
            RoleName: candidate.role ? candidate.role.toUpperCase() : "Administrator",
            RoleCode: roleCode || "SALES_EXEC",
          },
        IsActive: candidate.status !== false && candidate.IsActive !== false,
        IsSuspended: candidate.IsSuspended || false,
        permissions: candidate.permissions || candidate.Permissions || [],
        data_scopes: candidate.data_scopes || candidate.DataScopes || {},
        discount_limit_pct: Number(candidate.discount_limit_pct || 0),
        must_change_password: !!candidate.must_change_password,
      };
    }
    return null;
  };

  const isSuperAdmin = (u: AuthUser | null): boolean => {
    if (!u) return false;
    const code = String(u.UserRole?.RoleCode || "").toUpperCase();
    return code === "SUPER_ADMIN";
  };

  const hasPermission = useCallback((key: string): boolean => {
    if (!user) return false;
    if (isSuperAdmin(user)) return true;
    return Boolean(user.permissions && user.permissions.includes(key));
  }, [user]);

  const hasAnyPermission = useCallback((keys: string[]): boolean => {
    if (!user) return false;
    if (isSuperAdmin(user)) return true;
    if (!user.permissions) return false;
    return keys.some((k) => user.permissions!.includes(k));
  }, [user]);

  const hasAllPermissions = useCallback((keys: string[]): boolean => {
    if (!user) return false;
    if (isSuperAdmin(user)) return true;
    if (!user.permissions) return false;
    return keys.every((k) => user.permissions!.includes(k));
  }, [user]);

  const getScope = useCallback((resource: string): string => {
    if (!user) return "own";
    if (isSuperAdmin(user)) return "all";
    return user.data_scopes?.[resource] || "own";
  }, [user]);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const res = await api.get("/api/auth/me");
      const parsed = extractUser(res);
      setUser(parsed);
      return parsed;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Any request rejected for an ended session signs the user out; ProtectedRoute then shows the login page
  const { error: toastError } = useToast();
  const userRef = useRef(user);
  userRef.current = user;
  useEffect(() => {
    setSessionEndedHandler((message) => {
      if (!userRef.current) return;
      userRef.current = null;
      setUser(null);
      toastError("Signed out", message || "Your session has ended. Please log in again.");
    });
    return () => setSessionEndedHandler(null);
  }, [toastError]);

  const login = async (
    emailOrUsername: string,
    password: string,
    _rememberMe = false
  ): Promise<AuthUser> => {
    const res = await api.post("/api/auth/login", {
      email: emailOrUsername,
      password,
    });

    const parsed = extractUser(res);
    if (parsed) {
      setUser(parsed);
      return parsed;
    }
    throw new Error(res?.message || "Invalid login credentials");
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      // Ignore network errors on logout
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getScope,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function usePermission(permissionKey: string): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permissionKey);
}

export function useScope(resource: string): string {
  const { getScope } = useAuth();
  return getScope(resource);
}
